import React, { useEffect, useRef, useState } from 'react';
import { googleMapsNs, isGoogleMapsBlocked, loadGoogleMaps, type GoogleMap, type GoogleMarker } from './googleMaps';

type PinDropMapProps = {
  apiKey: string;
  lat?: string;
  lon?: string;
  defaultCenter?: { lat: number; lon: number };
  onPin: (lat: number, lon: number) => void;
  onReady?: () => void;
  onBlocked?: () => void;
  tall?: boolean;
};

const DEFAULT_CENTER = { lat: 12.9716, lng: 77.5946 };

const PinDropMap: React.FC<PinDropMapProps> = ({ apiKey, lat, lon, defaultCenter, onPin, onReady, onBlocked, tall }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<GoogleMap | null>(null);
  const markerRef = useRef<GoogleMarker | null>(null);
  const onPinRef = useRef(onPin);
  onPinRef.current = onPin;

  const parsedLat = lat ? Number(lat) : NaN;
  const parsedLon = lon ? Number(lon) : NaN;
  const hasPin = Number.isFinite(parsedLat) && Number.isFinite(parsedLon);

  const [blocked, setBlocked] = useState(isGoogleMapsBlocked());

  useEffect(() => {
    if (!apiKey || !containerRef.current || blocked) {
      return;
    }
    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then((google) => {
        if (cancelled || !containerRef.current || mapRef.current || isGoogleMapsBlocked()) {
          if (isGoogleMapsBlocked()) {
            setBlocked(true);
          }
          return;
        }
        const center = hasPin
          ? { lat: parsedLat, lng: parsedLon }
          : defaultCenter
            ? { lat: defaultCenter.lat, lng: defaultCenter.lon }
            : DEFAULT_CENTER;
        const map = new google.maps.Map(containerRef.current, {
          center,
          zoom: hasPin ? 16 : defaultCenter ? 14 : 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        window.setTimeout(() => {
          const failed = isGoogleMapsBlocked() || Boolean(containerRef.current?.querySelector('.gm-err-container'));
          if (failed) {
            setBlocked(true);
            onBlocked?.();
            mapRef.current = null;
            return;
          }
          map.addListener('click', (e) => {
            onPinRef.current(e.latLng.lat(), e.latLng.lng());
          });
          if (hasPin) {
            markerRef.current = new google.maps.Marker({
              position: { lat: parsedLat, lng: parsedLon },
              map,
              draggable: true,
            });
            markerRef.current.addListener('dragend', () => {
              const pos = markerRef.current?.getPosition();
              if (pos) {
                onPinRef.current(pos.lat(), pos.lng());
              }
            });
          }
          mapRef.current = map;
          google.maps.event.trigger(map, 'resize');
          onReady?.();
        }, 400);
      })
      .catch(() => {
        if (!cancelled) {
          setBlocked(true);
          onBlocked?.();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey, blocked]);

  useEffect(() => {
    const map = mapRef.current;
    const google = googleMapsNs();
    if (!map || !hasPin || !google) {
      return;
    }
    const position = { lat: parsedLat, lng: parsedLon };
    map.setCenter(position);
    map.setZoom(16);
    if (markerRef.current) {
      markerRef.current.setPosition(position);
    } else {
      const marker = new google.maps.Marker({
        position,
        map,
        draggable: true,
      });
      marker.addListener('dragend', () => {
        const pos = marker.getPosition();
        if (pos) {
          onPinRef.current(pos.lat(), pos.lng());
        }
      });
      markerRef.current = marker;
    }
    setTimeout(() => google.maps.event.trigger(map, 'resize'), 80);
  }, [hasPin, parsedLat, parsedLon]);

  if (!apiKey || blocked) {
    return (
      <div className={`pin-drop-map${tall ? ' pin-drop-map--tall' : ''}`}>
        <div className="pin-drop-map__canvas pin-drop-map__canvas--empty">
          Map couldn't load. Use current location to continue.
        </div>
        <p className="pin-drop-map__hint">Use current location to drop a pin, then confirm.</p>
      </div>
    );
  }

  return (
    <div className={`pin-drop-map${tall ? ' pin-drop-map--tall' : ''}`}>
      <div ref={containerRef} className="pin-drop-map__canvas" />
      <p className="pin-drop-map__hint">Move the pin to your exact delivery location.</p>
    </div>
  );
};

export default PinDropMap;
